import { ButtonType, ButtonWidget } from '@tableau/widgets';
import React from 'react';

import { EditRemoteAgentPoolDialogBody, EditRemoteAgentPoolDialogProps, EditRemoteAgentPoolDialogState } from '~src/app/content/EditRemoteAgentPoolDialog';
import { Messages } from '~src/codegen/Localize';
import { imgPath } from '~src/common/utilities/ImgUrl';
import { openReactActionDialog } from '~src/commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { FieldArrayRenderProps, FormikContextType } from '~src/settings/FormikWrapperForStubbing';
import { IRemoteQueryAgent } from '~src/settings/types/ISiteAdminBridgeSettings';
import { PageHeaderStyles } from '~src/spacesPlatform/components/layout/PageHeaderStyles';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { EdgePool } from '~src/ts/resources/EdgePool';
import { IUpdateEdgePoolResult } from '~src/ts/ServerApi/requests/UpdateEdgePoolRequest';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';

import { SiteBridgeSettingsForSiteAdminProps } from '../../../bridge-settings/BridgeSiteSettings';
import { PoolAssignmentOption } from '../types/PoolAssignmentOption';
import { PoolID, SystemDefinedPoolID } from '../types/PoolID';

export type BridgeRemoteAgentsEditButtonProps = Readonly<{
  agent: IRemoteQueryAgent;
  index: number;
  arrayHelpers: FieldArrayRenderProps;
  formik: FormikContextType<SiteBridgeSettingsForSiteAdminProps>;
  isDefaultPool: boolean;
  poolTestId: string;
  poolOptions: ReadonlyArray<PoolAssignmentOption>;
  siteId: string;
  agentPoolId: PoolID;
  refreshEdgePools: () => void;
}>;

export function BridgeRemoteAgentsEditButton(props: BridgeRemoteAgentsEditButtonProps) {
  return <>
    {FeatureFlags.edgeMultiPoolEnabled ?
      <span className={'tb-data-grid-icon tb-padded-left-large'} data-tb-test-id={'agent-edit-cell' + props.poolTestId}>
        {renderEditRemoteAgentButton(props)}
      </span>
      : null
    }
  </>;
}


function renderEditRemoteAgentButton(props: BridgeRemoteAgentsEditButtonProps) {
  const { isDefaultPool, poolTestId } = props;
  return (
    <ButtonWidget
      title={Messages.edit()}
      buttonType={isDefaultPool ? ButtonType.Icon : ButtonType.LowEmphasis}
      testId={'edit-remote-agent-button' + poolTestId}
      onClick={() => openEditRemoteAgentDialog(props)}
      style={{ alignItems: 'stretch' }}
    >
      {isDefaultPool ?
        <img src={imgPath('pencil_14px_small.svg')} alt={Messages.edit()} />
        :
        <span>
          <img src={imgPath('arrow_right_14px-small.svg')} style={PageHeaderStyles.iconForTextButton} alt={Messages.edit()} />
          {!isDefaultPool && Messages.siteRemoteAgentSettingsAssignToPool()}
        </span>
      }
    </ButtonWidget>
  );
}

function openEditRemoteAgentDialog(props: BridgeRemoteAgentsEditButtonProps) {
  const { agent, index, arrayHelpers, formik, poolOptions, siteId } = props;
  const action = FeatureFlags.edgeMultiPoolEnabled ?
    // Use Edge UpdatePool API to update agents
    (_: EditRemoteAgentPoolDialogProps, state: EditRemoteAgentPoolDialogState) => {
      EdgePool.updatePool({
        poolId: state.selectedPoolId,
        displayName: null,
        domainChange: null,
        agentChange: {agentId: agent.id},
        siteId: siteId,
        bridgeSettingsVersion: '0'
      }).then((result: IUpdateEdgePoolResult) => {
        props.refreshEdgePools();
        if (result.success) {
          ToasterService.instance.pop({ type: ToastType.Success, text: Messages.editRemoteAgentPoolSuccess() });
        }
        if (result.error) {
          ToasterService.instance.pop({ type: ToastType.Error, text: Messages.errorUnknown() });
          // TODO - ndepass 9/20/21 - Story 1297102: [BridgeSiteSettings] Create Multi-Pool table
        }
      });
      return Promise.resolve();
    }
    :
    // Use formik to update agents
    (_: EditRemoteAgentPoolDialogProps, state: EditRemoteAgentPoolDialogState) => {
      arrayHelpers.replace(index, { ...agent, pooled: state.selectedPoolId === SystemDefinedPoolID.Default });
      ToasterService.instance.pop({ type: ToastType.Success, text: Messages.editRemoteAgentPoolSuccess() });
      return formik.submitForm();
    };

  const dialogInstance = openReactActionDialog({
    title: Messages.editRemoteAgentPoolDialogTitle(),
    bodyComponent: EditRemoteAgentPoolDialogBody,
    initialBodyProps: { agent, poolOptions },
    initialDialogState: { selectedPoolId: props.agentPoolId },
    actionName: Messages.save(),
    action: action,
    actionDisabled: (_, state) => !state.selectedPoolId,
    dismissDialogOnActionFailed: true,
    dialogWidth: TabDialogWidth.width375px,
  });

  return Promise.resolve(dialogInstance.result);
}
